// Plain-language copy for the Data QA page. Every check line follows one rule:
// a plain title, what it means for the client's plan, and a concrete next step.
// Numbers and the econ check names (identifiability, decoupling, correlation)
// only appear in `why`, the click-through receipts. The row verdict (Readiness),
// the drill-down Bottom line and the lines all classify through here, so the
// table and the modal can never disagree.
import {
  DataQaGroup,
  DataQaThresholds,
  DataQaWarning,
  DecouplingTrigger,
  Metric,
  Pairing,
  RevenueFlag
} from '../dataQaContract';
import { grainLabel, identifiabilityFlag, isReadyAtFinest } from './data-qa-utils';
import { classifyPath } from './qa-summary';

// ---- row verdict ---------------------------------------------------------------

export type Readiness = 'good' | 'check' | 'directional' | 'not_enough';

export const READINESS_LABEL: Record<Readiness, string> = {
  good: 'Good to go',
  check: 'Check after run',
  directional: 'Budget reads are directional',
  not_enough: 'Not enough data'
};

/** Sort key, worst first. */
export const READINESS_RANK: Record<Readiness, number> = {
  not_enough: 3,
  directional: 2,
  check: 1,
  good: 0
};

const outcomeName = (kpi: string, p: Pairing) =>
  `${kpi} ${p === 'spend_volume' ? 'volume' : 'revenue'}`;

const bothOutcomes = (kpi: string) => `${kpi} volume and revenue`;

interface Concern {
  title: string;
  level: 'check' | 'directional';
}

// What moves a row off "Good to go": the spend-response risks a confidence
// interval can't express (spend's effect hard to measure, out of sync). Weak
// week-to-week tracking, revenue reliability, last year's level, a quiet feed
// and negative days are notes in the drill-down only; a quiet feed in particular
// is common enough that counting it would drown the spend signal.
const concerns = (g: DataQaGroup): Concern[] => {
  const out: Concern[] = [];
  const ident = identifiabilityFlag(g);
  if (ident) {
    out.push({
      title: "Hard to measure spend's effect",
      level: ident === 'at_risk' ? 'directional' : 'check'
    });
  }
  const outOfSync = g.warnings.filter(
    w => w.code === 'decoupling' && (w.severity === 'moderate' || w.severity === 'high')
  );
  if (outOfSync.length) {
    out.push({
      title: `${g.kpi} went out of sync with spend for a stretch`,
      level: outOfSync.some(w => w.severity === 'high') ? 'directional' : 'check'
    });
  }
  return out;
};

export const readiness = (g: DataQaGroup): Readiness => {
  if (classifyPath(g) === 'none') {
    return 'not_enough';
  }
  const cs = concerns(g);
  if (cs.some(c => c.level === 'directional')) {
    return 'directional';
  }
  return cs.length ? 'check' : 'good';
};

/** Pass the level when it's already computed (the table does, per row). */
export const readinessTooltip = (g: DataQaGroup, r: Readiness = readiness(g)): string => {
  if (r === 'not_enough') {
    return "Too little history to forecast or scale this combo. Open it to see what's missing.";
  }
  if (r === 'good') {
    return 'The history looks clean.';
  }
  return concerns(g)
    .map(c => c.title)
    .join(' · ');
};

// ---- drill-down Bottom line ----------------------------------------------------

/** Linear TV is offered Scaling only (econ emits `scaling_only_channel`). */
export const isScalingOnlyChannel = (g: DataQaGroup): boolean =>
  g.warnings.some(w => w.code === 'scaling_only_channel');

export const LINEAR_TV_COPY =
  "Linear TV: use Scaling. Forecasts aren't available for linear TV yet.";

export const bottomLine = (g: DataQaGroup): string => {
  const r = readiness(g);
  if (r === 'not_enough' && isScalingOnlyChannel(g)) {
    return "Forecasts aren't available for linear TV yet, and there isn't enough history for Scaling either. Check back once there's more.";
  }
  if (r === 'not_enough') {
    return "Not enough history to forecast or scale this combo yet. Check back once there's more history, or look at a coarser combo.";
  }
  const cls = classifyPath(g);
  const rollup =
    !isReadyAtFinest(g.path.forecast) && g.path.forecast.recommended_grain !== 'none'
      ? grainLabel(g.path.forecast.recommended_grain)
      : null;
  let lead = '';
  if (isScalingOnlyChannel(g)) {
    lead = `${LINEAR_TV_COPY} `;
  } else if (cls === 'scalingOnly') {
    lead =
      "This combo can't be forecast, but Scaling works: use a Scaling analysis for budget questions. ";
  } else if (rollup) {
    lead = `This exact slice is too thin to forecast, but the ${rollup} rollup works: request that instead. `;
  }
  if (r === 'good') {
    // A quiet feed or shaky revenue doesn't change the verdict, but "share it as
    // is" would contradict its "Worth checking" note below.
    const tail = hasWorthCheckingNote(g)
      ? "Spend's effect reads clearly; glance at the notes below before you share."
      : 'The history looks clean, so you can run it and share it as is.';
    return `${lead}${lead ? '' : 'Good to go. '}${tail}`;
  }
  if (r === 'check') {
    return `${lead}${lead ? '' : 'Good to forecast. '}A couple of things in the history are worth a look before you share results; they're listed below.`;
  }
  if (cls === 'scalingOnly') {
    return `${lead}Treat the curve's "what if we spend more or less" numbers as directional, and say so when you share them.`;
  }
  return `${lead}You can run it at your planned budget. Treat any "what if we spend more or less" numbers as directional, and say so when you share them.`;
};

// ---- check lines ---------------------------------------------------------------

export type LineGroup = 'before' | 'note' | 'fine';

export type Verdict = 'Usually fine' | 'Worth checking' | 'Limits budget reads';

export interface CheckLine {
  key: string;
  group: LineGroup;
  verdict?: Verdict;
  title: string;
  body?: string;
  next?: string;
  /** Click-through receipts: the only place numbers and check names appear. */
  why?: string;
}

// The app has no post-run "did the model find spend's effect" signal for CS
// (Model QA's trust rating stays econ-side, and Spend Relationship shows
// Diminishing even at a tiny effect), so the advice stays about how to share.
const whatIfNumbers = (scaling: boolean) =>
  scaling
    ? `the curve's "what if we spend more or less" numbers`
    : `"what if we spend more or less" numbers`;

const fmt2 = (v: number | null | undefined) => (v == null ? '–' : v.toFixed(2));

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(v);

// One line when both outcomes give the same result, else one per outcome.
const groupByResult = <T>(
  items: { pairing: Pairing; result: T }[],
  same: (a: T, b: T) => boolean
): { pairings: Pairing[]; result: T }[] => {
  if (items.length === 2 && same(items[0].result, items[1].result)) {
    return [{ pairings: [items[0].pairing, items[1].pairing], result: items[0].result }];
  }
  return items.map(i => ({ pairings: [i.pairing], result: i.result }));
};

const PAIRINGS: Pairing[] = ['spend_volume', 'spend_revenue'];

const nameFor = (kpi: string, pairings: Pairing[]) =>
  pairings.length === 2 ? bothOutcomes(kpi) : outcomeName(kpi, pairings[0]);

// Identifiability flags on either of two things: spend mostly followed a long
// trend (its trend share is high), or, once trend and seasonality are set
// aside, spend's ups and downs barely line up with the outcome. The body says
// which, since "trend drives it" is wrong for the second.
const TREND_SHARE_FLAG = 70;

const trendLines = (g: DataQaGroup, scaling: boolean): CheckLine[] => {
  const flagged = PAIRINGS.flatMap(p => {
    const w = g.warnings.find(x => x.code === 'identifiability' && x.pairing === p);
    return w?.flag === 'caution' || w?.flag === 'at_risk' ? [{ pairing: p, result: w.flag }] : [];
  });
  const why = (pairings: Pairing[]) =>
    `Econ check: identifiability. ${pairings
      .map(p => {
        const s = g.detail.identifiability[p];
        const co =
          s?.cor_remainder == null
            ? ''
            : ` once trend and seasonality are removed, spend and the outcome move together at ${fmt2(
                s.cor_remainder
              )} (we look for 0.30 or more);`;
        const trend =
          s?.pct_trend == null
            ? ''
            : ` trend is ${Math.round(s.pct_trend)}% of spend's movement (we look for ${TREND_SHARE_FLAG}% or less).`;
        return `${outcomeName(g.kpi, p)}:${co}${trend}`;
      })
      .join(' ')}`;
  if (!flagged.length) {
    return [
      {
        key: 'trend-ok',
        group: 'fine',
        title: "Spend's effect stands out in the history",
        why: why(['spend_volume', 'spend_revenue'])
      }
    ];
  }
  return groupByResult(flagged, (a, b) => a === b).map(({ pairings, result }) => {
    const o = nameFor(g.kpi, pairings);
    const atRisk = result === 'at_risk';
    const pct = g.detail.identifiability[pairings[0]]?.pct_trend;
    const cause =
      pct != null && pct > TREND_SHARE_FLAG
        ? `Spend has mostly moved in a long, steady trend rather than week to week, so its effect is tangled up with everything else that changed over that time.`
        : `Once the longer-term trend and the seasons are set aside, spend's ups and downs barely line up with ${o}.`;
    const impact = atRisk
      ? `So we can't tell how ${o} would respond to more or less spend.`
      : `${scaling ? 'The curve' : 'The forecast at your planned budget'} is still usable; it's harder to say how much more or less spend would change results.`;
    return {
      key: `trend-${pairings.join('-')}`,
      group: 'before',
      verdict: atRisk ? 'Limits budget reads' : 'Worth checking',
      title: "Hard to measure spend's effect",
      body: `${cause} ${impact}`,
      next: atRisk
        ? `Don't lead with ${whatIfNumbers(scaling)}; if you share them, call them directional. ${
            scaling ? '' : 'The forecast at your planned budget is fine to use. '
          }A coarser grain${scaling ? '' : ' or Scaling'} won't change this.`
        : `Run it as usual. If you share ${whatIfNumbers(scaling)} for big budget changes, call them directional. A coarser grain${
            scaling ? '' : ' or Scaling'
          } won't change this.`,
      why: why(pairings)
    };
  });
};

const TRIGGER_BODY: Record<DecouplingTrigger, (o: string) => string> = {
  negative_correlation: o => `${o} moved the opposite way from spend.`,
  low_spend_active_outcome: o => `${o} kept coming in while spend was close to nothing.`,
  flat_budget: o =>
    `spend was held flat while ${o} moved, so that stretch says little about how results respond to spend.`,
  none: o => `spend and ${o} didn't move together.`
};

interface OutOfSync {
  severity: 'moderate' | 'high';
  trigger: DecouplingTrigger;
  days: number | null;
  start: string | null;
  end: string | null;
}

// Merge the two outcomes only when their stretches overlap: one merged date
// range over stretches that fell apart would point the CP at the wrong weeks.
const overlaps = (a: OutOfSync, b: OutOfSync) =>
  a.severity === b.severity &&
  a.trigger === b.trigger &&
  (a.start == null || b.start == null
    ? a.start == null && b.start == null
    : a.start <= (b.end ?? b.start) && b.start <= (a.end ?? a.start));

const LONG_STRETCH_DAYS = 180;

const outOfSyncLines = (g: DataQaGroup): CheckLine[] => {
  const flagged = PAIRINGS.flatMap(p => {
    const w = g.warnings.find(x => x.code === 'decoupling' && x.pairing === p);
    if (!w || (w.severity !== 'moderate' && w.severity !== 'high')) {
      return [];
    }
    const d = g.detail.decoupling[p];
    return [
      {
        pairing: p,
        result: {
          severity: w.severity,
          trigger: d?.trigger ?? 'none',
          days: d?.max_run_days ?? null,
          start: d?.longest_run_start ?? null,
          end: d?.longest_run_end ?? null
        }
      }
    ];
  });
  if (!flagged.length) {
    return [
      {
        key: 'sync-ok',
        group: 'fine',
        title: `Spend and ${g.kpi} stayed in sync`
      }
    ];
  }
  const byPairing = new Map(flagged.map(f => [f.pairing, f.result]));
  return groupByResult(flagged, overlaps).map(({ pairings, result }) => {
    const spans = pairings.flatMap(p => {
      const s = byPairing.get(p);
      return s ? [s] : [];
    });
    const starts = spans.map(s => s.start).filter((s): s is string => s != null);
    const ends = spans.map(s => s.end).filter((s): s is string => s != null);
    const start = starts.length ? starts.sort()[0] : null;
    const end = ends.length ? ends.sort()[ends.length - 1] : null;
    const days = Math.max(...spans.map(s => s.days ?? 0));
    const o = nameFor(g.kpi, pairings);
    const span = start && end ? `${fmtDate(start)} to ${fmtDate(end)}` : null;
    // A stretch covering most of the history isn't an event to ask about.
    const longHaul = days >= LONG_STRETCH_DAYS;
    let when = span ? `Around ${span}` : `For a stretch of the history`;
    if (longHaul) {
      when = `For most of the history${span ? ` (${span})` : ''}`;
    } else if (!span && days > 0) {
      when = `For up to ${days} days in a row`;
    }
    const high = result.severity === 'high';
    return {
      key: `sync-${pairings.join('-')}`,
      group: 'before',
      verdict: high ? 'Limits budget reads' : 'Worth checking',
      title: `${o} went out of sync with spend for a stretch`,
      body: `${when}, ${TRIGGER_BODY[result.trigger](o)}`,
      next: longHaul
        ? `A stretch this long usually means spend for this combo is tracked somewhere else, or is very small. Check the source data before running.`
        : `Ask the client what happened ${
            span ? 'around those dates' : 'in that stretch'
          }: a sale or promo, a tracking or attribution change, a paused campaign. If it was a one-off, run it and mention it when you share. If spend or ${o} look wrong for that stretch, check the source data before running.`,
      why: `Econ check: decoupling. Longest stretch ${days} days${
        start && end ? ` (${start} to ${end})` : ''
      }. Flagged at 20+ days; 30+ days limits budget reads.`
    };
  });
};

const weeklyLines = (
  g: DataQaGroup,
  t: DataQaThresholds,
  divergence: boolean,
  scaling: boolean
): CheckLine[] => {
  const { weekly } = g.detail.correlation.spearman;
  const items = PAIRINGS.filter(p => weekly[p] != null)
    // A revenue-only miss is already explained by the revenue-reliability note.
    .filter(
      p =>
        !(
          divergence &&
          p === 'spend_revenue' &&
          g.detail.correlation.warn.spend_revenue &&
          !g.detail.correlation.warn.spend_volume
        )
    )
    .map(p => ({ pairing: p, result: g.detail.correlation.warn[p] }));
  const why = (pairings: Pairing[]) =>
    `Econ check: weekly correlation. ${pairings
      .map(p => `${outcomeName(g.kpi, p)} ${fmt2(weekly[p])}`)
      .join(', ')} (flagged below ${fmt2(t.correlation_warn_below)}).`;
  return groupByResult(items, (a, b) => a === b).map(({ pairings, result }): CheckLine => {
    const o = nameFor(g.kpi, pairings);
    return result
      ? {
          key: `weekly-${pairings.join('-')}`,
          group: 'note',
          verdict: 'Usually fine',
          title: `Weekly ups and downs in ${o} don't track spend closely`,
          body: "In a typical week, spend and results don't track as closely as we'd like. That's common: holidays, seasonality and longer trends move results too, and the model accounts for those.",
          next: `Run it. Nothing else to do unless another note on this combo also flags spend; then call ${whatIfNumbers(
            scaling
          )} directional when you share them.`,
          why: `${why(pairings)} This check on its own often flags combos the model handles fine.`
        }
      : {
          key: `weekly-ok-${pairings.join('-')}`,
          group: 'fine',
          title: `Weekly ups and downs in ${o} track spend`,
          why: why(pairings)
        };
  });
};

const REVENUE_FLAG_WHY: Record<RevenueFlag, string> = {
  low_correlation: 'weekly correlation',
  identifiability: "spend's effect hard to measure"
};

const revenueLine = (g: DataQaGroup, w: DataQaWarning | undefined): CheckLine[] => {
  if (!w) {
    return [];
  }
  const vol = outcomeName(g.kpi, 'spend_volume');
  const rev = outcomeName(g.kpi, 'spend_revenue');
  const rho = g.detail.correlation.spearman.weekly;
  return [
    {
      key: 'revenue',
      group: 'note',
      verdict: 'Worth checking',
      title: `${rev} is less reliable than ${vol}`,
      body: `${vol} follows spend, but ${rev} doesn't. Sometimes the platform reports a set value per conversion instead of real order dollars; sometimes order sizes just swing a lot.`,
      next: 'Plan on the Volume forecast (and CPA). If you need the revenue or ROAS forecast, confirm with the client where their revenue number comes from before sharing it.',
      why: `Revenue flagged on: ${(w.revenue_flags ?? [])
        .map(f => REVENUE_FLAG_WHY[f])
        .join(', ')}; volume passed both. Weekly correlation: volume ${fmt2(
        rho.spend_volume
      )}, revenue ${fmt2(rho.spend_revenue)}.`
    }
  ];
};

const lastYearLine = (g: DataQaGroup): CheckLine => {
  const cos = g.detail.seasonality.yoy.cosine_spend;
  if (!g.warnings.some(w => w.code === 'yoy_regime_shift')) {
    return { key: 'yoy-ok', group: 'fine', title: 'Spending is in line with last year' };
  }
  const timing = cos != null && cos < 0.8;
  return {
    key: 'yoy',
    group: 'note',
    verdict: 'Usually fine',
    title: timing
      ? 'Spend timing changed from last year'
      : 'Spending at a different level than last year',
    body: timing
      ? 'Spend lands at different times of year now (for example a flighting change), so last year is a weaker guide to timing.'
      : "You're spending noticeably more or less than last year, on the same seasonal rhythm. That's common when budgets change.",
    next: "Make sure the forecast budget reflects the client's plan. If the mix changed (say, a new upper-funnel push), mention it when you share.",
    why: `Econ check: year-over-year spend shift. Seasonal shape similarity ${fmt2(
      cos
    )} (a timing change is flagged below 0.80).`
  };
};

const isStale = (g: DataQaGroup) =>
  g.status.scaling.stale || g.warnings.some(w => w.code === 'stale_data');

/**
 * The notes that render with verdict "Worth checking" (freshness, revenue
 * reliability); the other notes are "Usually fine". Shared with bottomLine so a
 * Good-to-go row never says "share it as is" over one of them.
 */
const hasWorthCheckingNote = (g: DataQaGroup) =>
  isStale(g) || g.warnings.some(w => w.code === 'outcome_divergence');

const freshnessLine = (g: DataQaGroup, t: DataQaThresholds): CheckLine => {
  if (!isStale(g)) {
    return { key: 'fresh-ok', group: 'fine', title: 'Data is current through the latest week' };
  }
  return {
    key: 'fresh',
    group: 'note',
    verdict: 'Worth checking',
    title: 'Recent data is missing',
    body: 'This combo stopped reporting while the rest of the channel kept updating, so the most recent weeks are missing.',
    next: "Confirm the campaign is still running before you run it. If it ended, there's nothing to forecast; if it's live, check the platform connection or flag it to the data team.",
    why: `No new full week for more than ${t.scaling_stale_days} days while the channel kept reporting.`
  };
};

const MEASURE_NAME: Record<Metric, string> = {
  spend: 'spend',
  volume: 'volume',
  revenue: 'revenue'
};

// What a forecast of exactly this combo does with them (econ data_prep).
// A rollup sums the combos first, so a negative day can net out there.
const negativeEffect = (measures: Metric[]): string => {
  const spend = measures.includes('spend');
  const outcome = measures.includes('volume') || measures.includes('revenue');
  const parts = [
    spend ? 'counts negative spend as $0' : '',
    outcome ? 'leaves out the days with negative results' : ''
  ].filter(Boolean);
  return `A forecast of exactly this combo (not a rollup) ${parts.join(' and ')}.`;
};

const negativeLine = (g: DataQaGroup): CheckLine[] => {
  const w = g.warnings.find(x => x.code === 'negative_values');
  const nv = g.detail.negative_values;
  if (!w || !nv) {
    return [];
  }
  const measures = (w.measures ?? []).filter(m => nv[m]?.days);
  if (!measures.length) {
    return [];
  }
  const names = measures.map(m => MEASURE_NAME[m]);
  const list =
    names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return [
    {
      key: 'negative',
      group: 'note',
      verdict: 'Usually fine',
      title: 'Some days have negative numbers',
      body: `Some days show negative ${list}, which is usually refunds, credits or reversals. ${negativeEffect(
        measures
      )}`,
      next: "If that doesn't look right for this client, check the source data before sharing (the totals are under Why we flag this). Otherwise there's nothing to do.",
      why: measures
        .map(m => {
          const s = nv[m];
          const total =
            m === 'volume' ? Math.round(s.total).toLocaleString('en-US') : fmtMoney(s.total);
          const dates = s.first_date && s.last_date ? `, ${s.first_date} to ${s.last_date}` : '';
          return `${MEASURE_NAME[m]}: ${s.days} day${s.days === 1 ? '' : 's'}, ${total}${dates}`;
        })
        .join('; ')
    }
  ];
};

/**
 * The drill-down's check lines, grouped. Rows with no path at any grain get none:
 * stats on a few days of data (e.g. a 3-day series reading as perfectly
 * opposite) are noise, so the card says there isn't enough data instead.
 */
export const checkLines = (g: DataQaGroup, t: DataQaThresholds): CheckLine[] => {
  if (classifyPath(g) === 'none') {
    return [];
  }
  const divergence = g.warnings.find(w => w.code === 'outcome_divergence');
  const scaling = classifyPath(g) === 'scalingOnly';
  return [
    ...trendLines(g, scaling),
    ...outOfSyncLines(g),
    ...revenueLine(g, divergence),
    ...weeklyLines(g, t, Boolean(divergence), scaling),
    freshnessLine(g, t),
    lastYearLine(g),
    ...negativeLine(g)
  ];
};

// ---- display names -------------------------------------------------------------

/** Data QA's "N/A" tactic is what Request Forecast calls "Untagged". */
export const tacticLabel = (tactic: string): string => (tactic === 'N/A' ? 'Untagged' : tactic);
